'use client';

import { c as _c } from "react/compiler-runtime";
import type { FilmScenes } from '@/components/onboarding/onboarding-film';
import { useState } from 'react';
import type { Cue } from './pr-v2-onboarding-miniature';
import { PrV2OnboardingMiniature } from './pr-v2-onboarding-miniature';
import { typedAt, TYPING } from './pr-v2-onboarding-typing';
import { OnboardingFilm, OnboardingFilmDialogContent } from '@/components/onboarding/onboarding-film';
import { Button } from '@/components/ui/button';
import { Dialog, DialogTitle } from '@/components/ui/dialog';
import { safeLocalStorage } from '@/lib/safe-storage';
import { useAuth } from '@/providers/auth-provider';
export const PR_V2_ONBOARDING_DIALOG_STORAGE_KEY = 'pr-v2-onboarding-dialog-dismissed-v1';
function getUserStorageKey(userId: string | null): string {
  return userId ? `${PR_V2_ONBOARDING_DIALOG_STORAGE_KEY}:${userId}` : PR_V2_ONBOARDING_DIALOG_STORAGE_KEY;
}
function readIsDismissed(storageKey: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.localStorage.getItem(storageKey) === 'true';
}

// The film loops through these. The miniature shows every cue of the scenes before the playing
// one, plus the playing scene's cues that the clock has passed.
const SCENES: FilmScenes<Cue> = [{
  id: 'sorted',
  title: 'The most important changes come first',
  description: 'cubic groups the files by what they do and puts the core of the change on top.',
  durationMs: 4600,
  cues: [{
    atMs: 500,
    cue: 'sorted'
  }]
}, {
  id: 'explained',
  title: 'Each group explains itself',
  description: "A short note says what the group you're reading changes. A ? marks a call that needs your judgment.",
  durationMs: 4800,
  cues: [{
    atMs: typedAt(TYPING.title),
    cue: 'titleTyped'
  }, {
    atMs: typedAt(TYPING.body),
    cue: 'bodyTyped'
  }, {
    atMs: 2900,
    cue: 'meta'
  }, {
    atMs: 3300,
    cue: 'mark'
  }, {
    atMs: 3700,
    cue: 'link'
  }]
}, {
  id: 'ask',
  title: 'Ask about anything',
  description: "Ask the chat. It answers and lights up the lines it's talking about.",
  durationMs: 5200,
  cues: [{
    atMs: typedAt(TYPING.question) + 350,
    cue: 'sent'
  }, {
    atMs: 2650,
    cue: 'answer'
  }, {
    atMs: 3300,
    cue: 'lit'
  }]
}];
export function PrV2OnboardingDialog() {
  const $ = _c(17);
  const {
    user,
    isLoading: isAuthLoading
  } = useAuth();
  const t0 = user?.id ?? null;
  let t1;
  if ($[0] !== t0) {
    t1 = getUserStorageKey(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const storageKey = t1;
  let t2;
  if ($[2] !== storageKey) {
    t2 = () => readIsDismissed(storageKey);
    $[2] = storageKey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [isDismissed, setIsDismissed] = useState(t2);
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey);
  if (prevStorageKey !== storageKey) {
    setPrevStorageKey(storageKey);
    setIsDismissed(readIsDismissed(storageKey));
  }
  let t3;
  if ($[4] !== storageKey) {
    t3 = () => {
      safeLocalStorage.setItem(storageKey, "true");
      setIsDismissed(true);
    };
    $[4] = storageKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const dismiss = t3;
  let t4;
  if ($[6] !== dismiss) {
    t4 = isOpen => {
      if (!isOpen) {
        dismiss();
      }
    };
    $[6] = dismiss;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleOpenChange = t4;
  let t5;
  if ($[8] !== dismiss) {
    t5 = () => {
      dismiss();
    };
    $[8] = dismiss;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleDismiss = t5;
  const t6 = !isAuthLoading && !isDismissed;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <DialogTitle className="sr-only">A better way to review code</DialogTitle>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== handleDismiss) {
    t8 = <OnboardingFilmDialogContent>{t7}<OnboardingFilm scenes={SCENES} miniature={_temp} action={<Button type="button" autoFocus={true} onClick={handleDismiss}>Start reviewing</Button>} /></OnboardingFilmDialogContent>;
    $[11] = handleDismiss;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== handleOpenChange || $[14] !== t6 || $[15] !== t8) {
    t9 = <Dialog open={t6} onOpenChange={handleOpenChange}>{t8}</Dialog>;
    $[13] = handleOpenChange;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
function _temp(frame) {
  return <PrV2OnboardingMiniature frame={frame} />;
}